// DoctorsPage.js
import React, { useState, useEffect } from "react";
import { motion, useMotionValue } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../css/DoctorsPage.css";
import PawAnimation from "../components/PawAnimation";
import { LuUserRound } from "react-icons/lu";
import { TbUserHeart } from "react-icons/tb";
import { RiUserForbidLine } from "react-icons/ri";
import { IoMdAdd } from "react-icons/io";
import { FaStar } from "react-icons/fa";
import { RiDeleteBin6Fill } from "react-icons/ri";
import StatCard from "../components/StatCard";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import api from "../api"; // centralized API instance

export default function DoctorsPage() {
  const navigate = useNavigate();
  const BASE_URL = api.defaults.baseURL.replace("/api/auth", "");

  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);
  const x = useMotionValue(0);

  const [doctorList, setDoctorList] = useState([]);
  const [doctorsJoined, setDoctorsJoined] = useState([]);
  const [topDoctors, setTopDoctors] = useState([]);
  const [doctorStats, setDoctorStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState(null);

  // ✅ new modal for "See More"
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorSummary, setDoctorSummary] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  // === Fetch data from backend ===
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const response = await api.get("doctor-profiles/");
        const data = response.data;

        const uniqueJoined = (data.doctors_joined || []).filter(
          (doc, idx, arr) => arr.findIndex((d) => d.id === doc.id) === idx
        );

        setDoctorList(data.doctors || []);
        setDoctorsJoined(uniqueJoined);
        setTopDoctors(data.top_doctors || []);
        
        // ✅ Calculate stats from API response or doctor list
        const apiStats = data.doctor_stats || {};
        const allDoctors = data.doctors || [];
        
        // Calculate counts
        const total = apiStats.total || allDoctors.length || 0;
        
        // Calculate active count (only truly active, not suspended)
        const active = apiStats.active !== undefined 
          ? apiStats.active 
          : allDoctors.filter(doc => {
              const status = doc.status?.toLowerCase();
              return status === 'active';
            }).length;
        
        // ✅ Always count inactive from list to include suspended as inactive
        // Count inactive + suspended + disabled as inactive
        let inactiveCount;
        if (allDoctors.length > 0) {
          // Always count from doctor list to ensure suspended is included
          inactiveCount = allDoctors.filter(doc => {
            const status = doc.status?.toLowerCase();
            return status === 'inactive' || status === 'suspended' || status === 'disabled';
          }).length;
        } else if (apiStats.inactive !== undefined && apiStats.inactive !== null) {
          // If no doctor list but API provides inactive, use it
          inactiveCount = apiStats.inactive;
        } else if (total > 0 && active >= 0) {
          // Calculate from total - active as fallback
          inactiveCount = total - active;
        } else {
          inactiveCount = 0;
        }
        
        setDoctorStats({
          total: total,
          active: active,
          inactive: inactiveCount || 0,
        });
      } catch (error) {
        console.error("Error fetching doctors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  // === Delete doctor ===
  const handleOpenDeleteModal = (doctor) => {
    setDoctorToDelete(doctor);
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setDoctorToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!doctorToDelete) return;

    try {
      await api.delete(`admin/doctor-profiles/${doctorToDelete.id}/`);
      setDoctorList((prev) =>
        prev.filter((d) => String(d.id) !== String(doctorToDelete.id))
      );
    } catch (err) {
      console.error("Failed to delete doctor:", err);
      alert("Failed to delete doctor. Please try again.");
    }
    handleCloseDeleteModal();
  };

  const handleBack = () => navigate(-1);

  // === Slider logic ===
  const extendedDoctors = [...doctorsJoined, ...doctorsJoined];
  const cardWidth = 125;
  const totalWidth = cardWidth * doctorsJoined.length;

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
  };
  const rowVariants = {
    visible: { scale: 1, opacity: 1, transition: { type: "spring" } },
  };

  const handleDragEnd = () => {
    const offsetX = x.get();
    if (offsetX < -totalWidth) x.set(offsetX + totalWidth);
    else if (offsetX > 0) x.set(offsetX - totalWidth);
  };

  // === See More Modal Logic ===
  const handleSeeMore = async (event, doctor) => {
    try {
      const rect = event.currentTarget.getBoundingClientRect();
      setModalPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });

      setSelectedDoctor(doctor);
      const response = await api.get(`doctor-profiles/${doctor.id}/`);
      setDoctorSummary(response.data.appointment_fee_collection_summary);
      setIsSummaryModalOpen(true);
    } catch (error) {
      console.error("Error fetching doctor summary:", error);
      alert("Failed to fetch doctor summary.");
    }
  };

  const closeSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setSelectedDoctor(null);
    setDoctorSummary(null);
  };

  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />
        <div className="main-content">
          <div className="heading-sec">
            <div>
              <PageTitle title="Doctors" onBack={handleBack} />
              <p className="subtitle">Doctor Stats</p>
            </div>
            <div className="add-doctor-sec">
              <button
                className="add-doctor-btn"
                onClick={() => navigate("/add-doctor")}
              >
                <IoMdAdd className="add-icon" color="#EBA8BF" /> Add Doctor
              </button>
            </div>
          </div>

          {/* Doctor Stats */}
          <div className="doctor-stats">
            <div className="stats-container">
              <StatCard
                count={doctorStats.total}
                label="Total Doctors"
                color="#3B82F6"
                percentage={
                  doctorStats.total
                    ? (doctorStats.active / doctorStats.total) * 100
                    : 0
                }
                icon={<LuUserRound color="#3B82F6" />}
              />
              <StatCard
                count={doctorStats.active}
                label="Active Doctors"
                color="#10B981"
                percentage={
                  doctorStats.total
                    ? (doctorStats.active / doctorStats.total) * 100
                    : 0
                }
                icon={<TbUserHeart color="#36B570" />}
              />
              <StatCard
                count={doctorStats.inactive}
                label="Inactive Doctors"
                color="#F97316"
                percentage={
                  doctorStats.total
                    ? (doctorStats.inactive / doctorStats.total) * 100
                    : 0
                }
                icon={<RiUserForbidLine color="#FF7A1B" />}
              />
            </div>
          </div>

          <div className="doctors-section">
            <div className="doctors-section-left">
              {/* Joined Doctors */}
              <div className="doctors-joined-section">
                <h4>Doctors Joined</h4>
                <motion.div
                  className="slider-wrapper"
                  drag="x"
                  style={{ x }}
                  dragConstraints={{ left: -totalWidth, right: 0 }}
                  onDragEnd={handleDragEnd}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {doctorsJoined.map((doc, idx) => (
                    <motion.div
                      key={doc.id}
                      className="joined-card"
                      variants={rowVariants}
                      animate={{ scale: activeIndex === idx ? 1.2 : 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      onHoverStart={() => setActiveIndex(idx)}
                      onHoverEnd={() => setActiveIndex(null)}
                      style={{ width: cardWidth, flexShrink: 0 }}
                    >
                      <img
                        src={
                          doc.profile_image
                            ? doc.profile_image.startsWith("http")
                              ? doc.profile_image
                              : `${BASE_URL}${doc.profile_image}`
                            : "/default_profile.png"
                        }
                        alt={doc.name}
                      />
                      <p className="doc_name">{doc.name}</p>
                      <p className="doc_profile">{doc.specialization}</p>
                      <span>
                        {new Date(doc.created_at).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* Top Doctors */}
              <div className="top-doctors-section">
                <h4>Top Doctors</h4>
                <div className="top-doctors">
                  {topDoctors.map((doc) => (
                    <div className="top-doctor-card" key={doc.id}>
                      <div className="visit-tag clinic-visit">Clinic Visit</div>
                      <div className="doctor-info">
                        <img
                          src={doc.profile_image || "/default_profile.png"}
                          alt={doc.name}
                        />
                        <div className="doc-text">
                          <p className="doc-name">{doc.name}</p>
                          <span className="doc-profile">{doc.specialization}</span>
                          <div className="doc-rating-mainPage">
                            <div className="star-mainPage">
                              <FaStar />
                            </div>
                            <div className="rating-num">{doc.rating}</div>
                            <div className="reviews">
                              ({doc.reviews_count} reviews)
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="doc-tag-price">
                        <strong>
                          {doc.overall_fee
                            ? Number(doc.overall_fee).toLocaleString("en-IN", {
                                style: "currency",
                                currency: "INR",
                                minimumFractionDigits: 0,
                              })
                            : "₹---"}
                        </strong>

                        {/* ✅ Updated button with event */}
                        <button
                          className="see-more"
                          onClick={(e) => handleSeeMore(e, doc)}
                        >
                          See More...
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="doctors-section-right">
              <div className="table-section">
                <div className="table-header-doctors">
                  <h4>Doctors</h4>
                  <button
                    onClick={() =>
                      navigate("/all_doctor_screen", {
                        state: { doctors: doctorList },
                      })
                    }
                    className="see-all-link"
                  >
                    See All
                  </button>
                </div>

                <motion.table
                  className="doctor-table"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <thead>
                    <tr>
                      <th>Doctor Name</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctorList.slice(0, 14).map((doc) => (
                      <motion.tr
                        className="doctor-table-row"
                        key={doc.id}
                        variants={rowVariants}
                        whileHover={{
                          backgroundColor: "#9b59b6",
                          scale: 1.01,
                          color: "#fff",
                        }}
                        transition={{ duration: 0.2 }}
                        style={{ position: "relative" }}
                      >
                        <td>
                          <img
                            src={
                              doc.profile_image
                                ? doc.profile_image.startsWith("http")
                                  ? doc.profile_image
                                  : `${BASE_URL}${doc.profile_image}`
                                : "/default_profile.png"
                            }
                            alt={doc.name}
                          />
                          {doc.name}
                        </td>
                        <td>
                          <span className={`status ${doc.status.toLowerCase()}`}>
                            {doc.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="delete-btn"
                            onClick={() => handleOpenDeleteModal(doc)}
                          >
                            <RiDeleteBin6Fill className="delete_icon" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </motion.table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete Dr. ${doctorToDelete?.name}?`}
        message="Are you sure you want to remove this doctor? This action cannot be undone."
      />

      {/* ✅ Summary Modal (positioned below clicked button) */}
      {isSummaryModalOpen && selectedDoctor && doctorSummary && (
  <div
    className="summary-modal-overlay"
    onClick={closeSummaryModal} // click outside to close
  >
    <div
      className="summary-modal"
      style={{
        position: "absolute",
        top: `${modalPosition.top}px`,
        left: `${modalPosition.left}px`,
      }}
      onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
    >
      <h3>For This Week</h3>
      <p>
        <strong>Overall Appointments:</strong>
        <span>{doctorSummary.overall_appointment}</span>
      </p>
      <p>
        <strong>Overall Doctor Fees:</strong>
        <span>
          ₹{Number(doctorSummary.overall_doctor_fees).toLocaleString("en-IN")}
        </span>
      </p>
      <p>
        <strong>Pawfriends Commission:</strong>
        <span>{doctorSummary.pawfriends_commission_percentage}%</span>
      </p>
      <p>
        <strong>Pawfriends Profit:</strong>
        <span>
          ₹{Number(doctorSummary.pawfriends_profit).toLocaleString("en-IN")}
        </span>
      </p>
    </div>
  </div>
)}

    </div>
  );
}
